// Story BP-8 PC.6: sub-tab Telemetry dentro do AdminBillingPanel.
// Toggle Raw (últimos 100 events) | Aggregated (GROUP BY feature/tier).
// Consome system_logs WHERE category='upgrade_intent' (BP-5 telemetry).

import React, { useState, useEffect } from 'react';
import { Loader2, BarChart3, List, RefreshCw } from 'lucide-react';
import {
  fetchUpgradeTelemetry,
  type UpgradeIntentRawEvent,
  type UpgradeIntentAggregatedRow,
  type TelemetryMode,
} from '../../hooks/useAdminBilling';

export const AdminBillingTelemetry: React.FC = () => {
  const [mode, setMode] = useState<TelemetryMode>('aggregated');
  const [days, setDays] = useState(30);
  const [rawEvents, setRawEvents] = useState<UpgradeIntentRawEvent[]>([]);
  const [aggregated, setAggregated] = useState<UpgradeIntentAggregatedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchUpgradeTelemetry({ mode, days }).then(({ data, error: err }) => {
      if (cancelled) return;
      if (mode === 'raw') {
        setRawEvents(data as UpgradeIntentRawEvent[]);
      } else {
        setAggregated(data as UpgradeIntentAggregatedRow[]);
      }
      setError(err);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [mode, days, refreshKey]);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
          <button
            type="button"
            onClick={() => setMode('aggregated')}
            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
              mode === 'aggregated' ? 'bg-white/10 text-white' : 'text-neutral-400 hover:text-white'
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5" /> Agregado
          </button>
          <button
            type="button"
            onClick={() => setMode('raw')}
            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
              mode === 'raw' ? 'bg-white/10 text-white' : 'text-neutral-400 hover:text-white'
            }`}
          >
            <List className="w-3.5 h-3.5" /> Eventos crus
          </button>
        </div>

        {mode === 'aggregated' && (
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-base sm:text-xs bg-neutral-900 border border-white/10 rounded-lg px-2 py-1.5 text-white"
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
          </select>
        )}

        <button
          type="button"
          onClick={() => setRefreshKey(k => k + 1)}
          className="ml-auto p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5 transition-colors"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 text-neutral-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400">
          {error}
        </div>
      ) : mode === 'aggregated' ? (
        <AggregatedTable rows={aggregated} />
      ) : (
        <RawTable events={rawEvents} />
      )}
    </div>
  );
};

const AggregatedTable: React.FC<{ rows: UpgradeIntentAggregatedRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-neutral-500 italic py-8 text-center">
        Nenhum evento upgrade_intent nesse período.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-neutral-900/50 text-xs text-neutral-400 uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2">Feature</th>
            <th className="text-left px-3 py-2">De</th>
            <th className="text-left px-3 py-2">Para</th>
            <th className="text-right px-3 py-2">Eventos</th>
            <th className="text-right px-3 py-2">Users únicos</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-white/5">
              <td className="px-3 py-2 text-white font-medium">{r.feature}</td>
              <td className="px-3 py-2 text-neutral-300">{r.tier_current}</td>
              <td className="px-3 py-2 text-neutral-300">{r.tier_required}</td>
              <td className="px-3 py-2 text-right text-white tabular-nums">{r.count}</td>
              <td className="px-3 py-2 text-right text-neutral-300 tabular-nums">{r.unique_users}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RawTable: React.FC<{ events: UpgradeIntentRawEvent[] }> = ({ events }) => {
  if (events.length === 0) {
    return (
      <p className="text-sm text-neutral-500 italic py-8 text-center">
        Nenhum evento upgrade_intent recente.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-neutral-900/50 text-xs text-neutral-400 uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2">Quando</th>
            <th className="text-left px-3 py-2">User</th>
            <th className="text-left px-3 py-2">Feature</th>
            <th className="text-left px-3 py-2">De → Para</th>
            <th className="text-left px-3 py-2">CTA?</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {events.map(e => (
            <tr key={e.id} className="hover:bg-white/5">
              <td className="px-3 py-2 text-neutral-400 font-mono text-xs">
                {new Date(e.created_at).toLocaleString('pt-BR')}
              </td>
              <td className="px-3 py-2 text-neutral-300 font-mono text-xs">
                {e.user_id ? e.user_id.slice(0, 8) : '—'}
              </td>
              <td className="px-3 py-2 text-white">{e.metadata?.feature ?? '—'}</td>
              <td className="px-3 py-2 text-neutral-300">
                {(e.metadata?.tier_current ?? '—') + ' → ' + (e.metadata?.tier_required ?? '—')}
              </td>
              <td className="px-3 py-2">
                {e.metadata?.cta_clicked ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                    sim
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-700/50 text-neutral-400">
                    não
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
